'use client';

import { useCallback, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import posthog from 'posthog-js';
import { trpcVanilla } from '@/lib/trpc';
import {
  detectImageMimeFromMagicBytes,
  getFileExtension,
  toHexPrefix,
} from '../lib/detect-file-mime';

interface UploadProgress {
  loadedBytes: number;
  totalBytes: number;
  percent: number;
}

interface EntrantUploadInput {
  file: File;
  assetType: 'image' | 'video' | 'document';
  tags?: string[];
  signal?: AbortSignal;
  onProgress?: (progress: UploadProgress) => void;
}

interface EntrantUploadResult {
  asset: {
    id: string;
    cdnUrl: string;
    objectKey: string;
    status: string;
    [key: string]: unknown;
  };
}

async function entrantUploadAction(input: EntrantUploadInput): Promise<EntrantUploadResult> {
  const file = input.file;
  const declaredMimeType =
    file.type.split(';')[0]?.trim().toLowerCase() || 'application/octet-stream';
  const arrayBuffer = await file.arrayBuffer();

  let mimeType = declaredMimeType;
  if (input.assetType === 'image') {
    const bytes = new Uint8Array(arrayBuffer);
    const detectedMimeType = detectImageMimeFromMagicBytes(bytes);

    if (detectedMimeType && detectedMimeType !== declaredMimeType) {
      mimeType = detectedMimeType;
      try {
        posthog.capture('entrant_upload_mime_corrected', {
          asset_type: input.assetType,
          declared_mime: declaredMimeType,
          detected_mime: detectedMimeType,
          file_extension: getFileExtension(file.name),
          file_size_bytes: file.size,
          magic_hex_prefix: toHexPrefix(bytes),
          source: 'use-entrant-asset-upload',
        });
      } catch {
        // Observability should never break the upload flow.
      }
    }
  }

  // 1. Prepare upload (get presigned URL)
  const prepared = await trpcVanilla.myAssets.prepareUpload.mutate({
    filename: file.name,
    mimeType,
    sizeBytes: file.size,
    assetType: input.assetType,
    tags: input.tags ?? [],
  });

  // 2. Upload file to presigned URL
  input.onProgress?.({ loadedBytes: 0, totalBytes: file.size, percent: 0 });
  input.onProgress?.({ loadedBytes: file.size, totalBytes: file.size, percent: 50 });

  const uploadResponse = await fetch(prepared.upload.url, {
    method: prepared.upload.method,
    headers: prepared.upload.headers,
    body: arrayBuffer,
    signal: input.signal,
  });

  if (!uploadResponse.ok) {
    throw new Error(`Upload failed (${uploadResponse.status})`);
  }

  input.onProgress?.({ loadedBytes: file.size, totalBytes: file.size, percent: 75 });

  // 3. Complete upload (verify + mark as ready)
  const completed = await trpcVanilla.myAssets.completeUpload.mutate({
    assetId: prepared.asset.id,
  });

  input.onProgress?.({ loadedBytes: file.size, totalBytes: file.size, percent: 100 });

  return { asset: completed };
}

export function useEntrantAssetUpload() {
  const [progress, setProgress] = useState<UploadProgress | null>(null);

  const mutation = useMutation({
    mutationFn: entrantUploadAction,
  });

  const upload = useCallback(
    async (input: EntrantUploadInput) => {
      setProgress({ loadedBytes: 0, totalBytes: input.file.size, percent: 0 });

      const result = await mutation.mutateAsync({
        ...input,
        onProgress: (next) => {
          setProgress(next);
          input.onProgress?.(next);
        },
      });

      return result;
    },
    [mutation],
  );

  return {
    upload,
    progress,
    isUploading: mutation.isPending,
    isError: mutation.isError,
    error: mutation.error,
    data: mutation.data,
    reset: () => {
      setProgress(null);
      mutation.reset();
    },
  };
}
